import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  BookOpen, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  ChevronRight,
  Filter,
  Megaphone,
  MessageSquare,
  Truck,
  Users,
  BarChart3,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useApp } from '@/context/AppContext';

const dimensionIcons = {
  'Convocatoria': Megaphone,
  'Comunicación': MessageSquare,
  'Metodologías': Lightbulb,
  'Logística': Truck,
  'Aliados externos': Users,
  'Retroalimentación': BarChart3
};

const dimensionColors = {
  'Convocatoria': 'bg-primary/10 text-primary border-primary/20',
  'Comunicación': 'bg-secondary/10 text-secondary border-secondary/20',
  'Metodologías': 'bg-accent/10 text-accent border-accent/20',
  'Logística': 'bg-success/10 text-success border-success/20',
  'Aliados externos': 'bg-chart-5/10 text-chart-5 border-chart-5/20',
  'Retroalimentación': 'bg-destructive/10 text-destructive border-destructive/20'
};

export const ActionPlanPage = () => {
  const { 
    t, 
    language,
    getRecommendations,
    calculateScores,
    toggleCommitment,
    isCommitted,
    getAllCommitments,
    isCompleted
  } = useApp();

  const [selectedPractice, setSelectedPractice] = useState(null);
  const [filterDimension, setFilterDimension] = useState('all');

  const recommendations = useMemo(() => getRecommendations(), [getRecommendations]);
  const { dimensionDetails } = useMemo(() => calculateScores(), [calculateScores]);
  const commitments = getAllCommitments();

  // Filter practices by dimension
  const filteredPractices = useMemo(() => {
    if (filterDimension === 'all') return recommendations;
    return recommendations.filter(p => p.dimension === filterDimension);
  }, [recommendations, filterDimension]);

  // Get unique dimensions from recommendations
  const availableDimensions = useMemo(() => {
    const dims = [...new Set(recommendations.map(p => p.dimension))];
    return dims;
  }, [recommendations]);

  // Redirect if not completed
  if (!isCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="card-brand max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Complete the diagnosis first' : 'Completa el diagnóstico primero'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'en' 
                ? 'Please complete the questionnaire to access the action plan.'
                : 'Por favor completa el cuestionario para acceder al plan de acción.'}
            </p>
            <Button asChild className="btn-brand-primary">
              <a href="/questionnaire">{t('startDiagnosis')}</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            {t('actionPlanTitle')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('actionPlanSubtitle')}
          </p>
        </motion.div>

        <Tabs defaultValue="practices" className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-muted/50 p-1 rounded-2xl">
            <TabsTrigger 
              value="practices" 
              className="rounded-xl data-[state=active]:bg-card data-[state=active]:shadow-sm"
            >
              {t('recommendedPractices')}
            </TabsTrigger>
            <TabsTrigger 
              value="commitments"
              className="rounded-xl data-[state=active]:bg-card data-[state=active]:shadow-sm"
            >
              {t('myCommitments')} ({commitments.length})
            </TabsTrigger>
          </TabsList>

          {/* Practices Tab */}
          <TabsContent value="practices">
            {/* Filter and Download */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6"
            >
              <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {language === 'en' ? 'Filter by dimension' : 'Filtrar por dimensión'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  className="no-print flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {language === 'en' ? 'Download PDF' : 'Descargar PDF'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 no-print">
                <Button
                  variant={filterDimension === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterDimension('all')}
                  className={`rounded-full ${filterDimension === 'all' ? 'btn-brand-secondary' : ''}`}
                >
                  {language === 'en' ? 'All' : 'Todas'}
                </Button>
                {availableDimensions.map(dim => (
                  <Button
                    key={dim}
                    variant={filterDimension === dim ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterDimension(dim)}
                    className={`rounded-full ${filterDimension === dim ? 'btn-brand-secondary' : ''}`}
                  >
                    {language === 'en' ? t(`dimensions.${dim}`) : dim}
                  </Button>
                ))}
              </div>
            </motion.div>

            {/* Practices Grid */}
            <motion.div
              key={filterDimension}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {filteredPractices.map((practice) => {
                const Icon = dimensionIcons[practice.dimension] || Lightbulb;
                const colors = dimensionColors[practice.dimension];
                const practiceCommitments = (practice.tips || []).filter((_, i) => 
                  isCommitted(practice.id, i)
                ).length;
                
                return (
                  <motion.div key={practice.id} variants={itemVariants}>
                    <Card 
                      className="card-brand h-full cursor-pointer hover:shadow-lg group"
                      onClick={() => setSelectedPractice(practice)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-2xl ${colors.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`h-6 w-6 ${colors.split(' ')[1]}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${colors}`}>
                                {language === 'en' ? t(`dimensions.${practice.dimension}`) : practice.dimension}
                              </span>
                              {practiceCommitments > 0 && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                  {practiceCommitments} ✓
                                </span>
                              )}
                            </div>
                            <h3 className="font-bold text-foreground mb-2 group-hover:text-secondary transition-colors">
                              {language === 'en' ? practice.titleEn : practice.title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {language === 'en' 
                                ? practice.what_and_whyEn?.slice(0, 120) 
                                : practice.what_and_why?.slice(0, 120)}...
                            </p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-secondary group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </TabsContent>

          {/* Commitments Tab */}
          <TabsContent value="commitments">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {commitments.length === 0 ? (
                <Card className="card-brand">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                      <Lightbulb className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      {language === 'en' ? 'No commitments yet' : 'Aún no hay compromisos'}
                    </h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      {t('noCommitments')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Group commitments by dimension */}
                  {availableDimensions.map(dim => {
                    const dimCommitments = commitments.filter(c => c.dimension === dim);
                    if (dimCommitments.length === 0) return null;
                    
                    const Icon = dimensionIcons[dim];
                    const colors = dimensionColors[dim];
                    
                    return (
                      <Card key={dim} className="card-brand">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${colors.split(' ')[0]} flex items-center justify-center`}>
                              <Icon className={`h-5 w-5 ${colors.split(' ')[1]}`} />
                            </div>
                            <CardTitle className="text-lg">
                              {language === 'en' ? t(`dimensions.${dim}`) : dim}
                            </CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-3">
                            {dimCommitments.map((c, index) => (
                              <li key={`${c.practiceId}-${c.tipIndex}`} className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-foreground">{c.tip}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {c.practiceTitle}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>

        {/* Practice Detail Sheet */}
        <Sheet open={!!selectedPractice} onOpenChange={() => setSelectedPractice(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg bg-card overflow-y-auto">
            {selectedPractice && (
              <div className="space-y-6">
                <SheetHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${dimensionColors[selectedPractice.dimension]}`}>
                      {language === 'en' ? t(`dimensions.${selectedPractice.dimension}`) : selectedPractice.dimension}
                    </span>
                  </div>
                  <SheetTitle className="text-xl font-bold text-foreground text-left">
                    {language === 'en' ? selectedPractice.titleEn : selectedPractice.title}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    Detalles de la práctica
                  </SheetDescription>
                </SheetHeader>

                <ScrollArea className="h-[calc(100vh-200px)] pr-4">
                  <div className="space-y-6">
                    {/* Who Implements */}
                    <div className="p-4 bg-muted/30 rounded-2xl">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-secondary" />
                        <span className="text-sm font-semibold text-foreground">
                          {t('whoImplements')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en' ? selectedPractice.who_implementsEn : selectedPractice.who_implements}
                      </p>
                    </div>

                    {/* What and Why */}
                    <div className="p-4 bg-muted/30 rounded-2xl">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="h-4 w-4 text-secondary" />
                        <span className="text-sm font-semibold text-foreground">
                          {t('whatAndWhy')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {language === 'en' ? selectedPractice.what_and_whyEn : selectedPractice.what_and_why}
                      </p>
                    </div>

                    {/* Results */}
                    <div className="p-4 bg-success/5 border border-success/20 rounded-2xl">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-success" />
                        <span className="text-sm font-semibold text-foreground">
                          {t('resultsAchieved')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {language === 'en' ? selectedPractice.results_achievedEn : selectedPractice.results_achieved}
                      </p>
                    </div>

                    {/* Challenge and Solution */}
                    <div className="space-y-3">
                      <div className="p-4 bg-warning/5 border border-warning/20 rounded-2xl">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <span className="text-sm font-semibold text-foreground">
                            {t('challengeFaced')}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {language === 'en' ? selectedPractice.challengeEn : selectedPractice.challenge}
                        </p>
                      </div>

                      <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-2xl">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-secondary" />
                          <span className="text-sm font-semibold text-foreground">
                            {t('solutionResponse')}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {language === 'en' ? selectedPractice.solution_responseEn : selectedPractice.solution_response}
                        </p>
                      </div>
                    </div>

                    {/* Tips with Checkboxes */}
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-2xl">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">
                          {t('tipsToApply')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-4">
                        {t('selectTipsToCommit')}
                      </p>
                      <div className="space-y-3">
                        {(language === 'en' ? selectedPractice.tipsEn : selectedPractice.tips)?.map((tip, index) => (
                          <label
                            key={index}
                            className="flex items-start gap-3 cursor-pointer group"
                          >
                            <Checkbox
                              checked={isCommitted(selectedPractice.id, index)}
                              onCheckedChange={() => toggleCommitment(selectedPractice.id, index)}
                              className="mt-0.5 border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <span className={`text-sm leading-relaxed ${
                              isCommitted(selectedPractice.id, index) 
                                ? 'text-foreground' 
                                : 'text-muted-foreground group-hover:text-foreground'
                            } transition-colors`}>
                              {tip}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Print-only Section - Hidden on screen, visible when printing */}
        <div className="hidden print:block">
          <div className="text-center mb-8 pb-4 border-b-2 border-gray-300">
            <h1 className="text-2xl font-bold mb-2">
              {language === 'en' ? 'Best Practices Guide' : 'Guía de Buenas Prácticas'}
            </h1>
            <p className="text-sm text-gray-600">
              {language === 'en' ? 'Raise Your Voice - LAB XXI' : 'Alza Tu Voz - LAB XXI'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {filteredPractices.map((practice, index) => (
            <div key={practice.id} className="mb-6 pb-4 border-b border-gray-200 page-break-inside-avoid">
              <div className="flex items-start gap-2 mb-3">
                <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded">
                  {language === 'en' ? t(`dimensions.${practice.dimension}`) : practice.dimension}
                </span>
              </div>
              
              <h2 className="text-lg font-bold mb-3">
                {index + 1}. {language === 'en' ? practice.titleEn : practice.title}
              </h2>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold text-gray-700">
                    👤 {language === 'en' ? 'Who can implement it?' : '¿Quién puede implementarla?'}
                  </p>
                  <p className="text-gray-600 ml-6">
                    {language === 'en' ? practice.who_implementsEn : practice.who_implements}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-gray-700">
                    📖 {language === 'en' ? 'What is it and why do we use it?' : '¿De qué trata y por qué la usamos?'}
                  </p>
                  <p className="text-gray-600 ml-6">
                    {language === 'en' ? practice.what_and_whyEn : practice.what_and_why}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-gray-700">
                    📈 {language === 'en' ? 'Results achieved' : 'Resultados obtenidos'}
                  </p>
                  <p className="text-gray-600 ml-6">
                    {language === 'en' ? practice.results_achievedEn : practice.results_achieved}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-gray-700">
                    ⚠️ {language === 'en' ? 'Challenge faced' : 'Desafío enfrentado'}
                  </p>
                  <p className="text-gray-600 ml-6">
                    {language === 'en' ? practice.challengeEn : practice.challenge}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-gray-700">
                    ✅ {language === 'en' ? 'How we solved it' : 'Cómo lo resolvimos'}
                  </p>
                  <p className="text-gray-600 ml-6">
                    {language === 'en' ? practice.solution_responseEn : practice.solution_response}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-gray-700">
                    💡 {language === 'en' ? 'Tips to apply this practice' : 'Tips para aplicar esta práctica'}
                  </p>
                  <ul className="ml-6 list-disc list-inside text-gray-600">
                    {(language === 'en' ? practice.tipsEn : practice.tips)?.map((tip, tipIndex) => (
                      <li key={tipIndex}>{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}

          <div className="mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
            <p>{language === 'en' 
              ? 'Generated by the Raise Your Voice platform - LAB XXI' 
              : 'Generado por la plataforma Alza Tu Voz - LAB XXI'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanPage;
